import { getLinkedinAccountById, triggerAccountVerification } from 'API/linkedin-accounts';
import { getFunnelById, updateFunnel } from 'API/linkedin-funnel';
import { ErrorMessages } from 'enums';
import { Funnel, FunnelFunctionFlow } from 'features/co-pilot/co-pilot.types';
import useLinkedinAccountLimits from 'hooks/use-linkedin-account-limits';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useReactFlow } from 'reactflow';
import useErrorStore from 'stores/error-store';
import { CampaignBuilderSteps, ContactToAdd } from './campaign-builder.types';
import { LinkedinAccountStatuses } from '../linkedin-accounts/linkedin-accounts.types';

/** Anfang der Server-Meldung, wenn das Speichern laufende Kontakte verwaisen ließe (campaigns.service). */
const FLOW_REMAP_REFUSED = 'Der Ablauf kann so nicht gespeichert werden';

const useCampaign = (handleOpenCopilotLicenseExpiredDialog?: () => void) => {
  useUserData();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { setNodes, setEdges, setViewport } = useReactFlow();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [campaign, setCampaign] = useState<Funnel | null>(null);
  const [campaignLoading, setCampaignLoading] = useState<boolean>(false);
  const [updateCampaignLoading, setUpdateCampaignLoading] = useState<boolean>(false);

  const [askForSyncDialog, setAskForSyncDialog] = useState<{ open: boolean; loading: boolean; accountId: string | null; userEmail: string | null }>({
    open: false,
    loading: false,
    accountId: null,
    userEmail: null,
  });

  const { fullLinkedinAccount, linkedinAccountLoading, linkedinAccountUpdating } = useLinkedinAccountLimits(
    campaign?.linkedinAccount ?? undefined,
    campaign?._id ?? undefined
  );

  const campaignId = searchParams.get('_id');
  const ongoingUpdatesRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Init funnel handlers
   */

  const fetchCampaign = async (campaignId: string) => {
    setCampaignLoading(true);
    const response = await getFunnelById(campaignId);

    if (response && !response.error) {
      const resFunnel: Funnel = response;

      const updates: Partial<Funnel> = {};
      const currentSettings = resFunnel.settings || {};
      if (_.isNil(currentSettings.swissS)) {
        updates.settings = {
          ...currentSettings,
          swissS: false,
        };
      }

      if (Object.keys(updates).length > 0) {
        await updateFunnel(resFunnel._id, updates);
        Object.assign(resFunnel, updates);
      }

      if (!_.isNil(resFunnel.linkedinAccount)) {
        const liAccount = await getLinkedinAccountById(resFunnel.linkedinAccount);
        if (!liAccount.error) {
          const campaignStepsArray = _.map(CampaignBuilderSteps, (tab) => tab);
          const campaignSummaryIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_SUMMARY);
          const stepIndexParam = new URLSearchParams(window.location.search).get('stepIndex');
          if (!_.isNil(stepIndexParam)) {
            const stepIndex = parseInt(stepIndexParam);
            if (stepIndex === campaignSummaryIndex && liAccount.status !== LinkedinAccountStatuses.SYNCHRONIZED) {
              handleOpenAskForSyncDialog(resFunnel.linkedinAccount, liAccount.userEmail);
            }
          }
        }
      }

      setCampaign(resFunnel);
    } else {
      handleOpenErrorDialog(response?.error, '/campaigns/:id', 'get');
    }
    setCampaignLoading(false);
  };

  useEffect(() => {
    if (!_.isNil(campaignId)) {
      fetchCampaign(campaignId);
    } else {
      setCampaign(null);
    }
  }, [campaignId]);

  const initSyncForLinkedinAccountById = async (accountId: string) => {
    if (!import.meta.env.DEV) {
      await triggerAccountVerification(accountId, 'high');
    }
  };

  /**
   * Update funnel handlers
   */

  /**
   * `stepIndex` gehört in denselben Schreibvorgang: Der Init-Effekt im Builder liest den
   * Schritt aus der URL, ein Nachreichen käme zu spät.
   */
  const setCampaignWithId = (newFunnel: Funnel, stepIndex?: number) => {
    setCampaign(newFunnel);

    setSearchParams(_.isNil(stepIndex) ? { _id: newFunnel._id } : { _id: newFunnel._id, stepIndex: String(stepIndex) });
  };

  const setCampaignQueryId = (funnelId: string) => {
    setSearchParams({ _id: funnelId });
  };

  const handleResetCampaign = () => {
    setSearchParams({}, { replace: true });
    setCampaign(null);
  };

  const handleUpdateCampaign = async (value: Partial<Funnel> | { addContacts: ContactToAdd[] } | { removeContacts: string[] }) => {
    if (_.isNil(campaign)) {
      window.alert(t('campaignNotFound'));
      return;
    }

    if (!_.isNil(timeoutRef.current)) {
      clearTimeout(timeoutRef.current);
    }

    setUpdateCampaignLoading(true);

    ongoingUpdatesRef.current += 1;

    const tempCampaign = { ...campaign };

    setCampaign((prev) => {
      if (!_.isNil(prev)) {
        return { ...prev, ...value };
      } else {
        return null;
      }
    });

    const res = await updateFunnel(campaign._id, value);

    if (!res.error) {
      timeoutRef.current = await new Promise((resolve) =>
        setTimeout(() => {
          ongoingUpdatesRef.current -= 1;

          if (ongoingUpdatesRef.current === 0) {
            setUpdateCampaignLoading(false);
          }

          resolve(timeoutRef.current);
        }, 1.5 * 1000)
      );
    } else {
      setCampaign(tempCampaign);

      if (!_.isNil(timeoutRef.current)) {
        clearTimeout(timeoutRef.current);
      }
      if (!_.isNil(ongoingUpdatesRef.current)) {
        ongoingUpdatesRef.current = 0;
      }

      setUpdateCampaignLoading(false);

      if (res?.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res?.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
        handleOpenCopilotLicenseExpiredDialog?.();
      } else {
        handleOpenErrorDialog(res?.error, '/campaigns/:id', 'patch');
      }
    }
  };

  const handleUpdateCampaignReactFlow = async (flow: FunnelFunctionFlow, signal: AbortSignal) => {
    if (_.isNil(campaign)) {
      window.alert(t('campaignNotFound'));
      return;
    }

    setUpdateCampaignLoading(true);

    const res = await updateFunnel(campaign._id, { reactFlow: flow }, signal);

    if (!res.error) {
      setCampaign((prev) => {
        if (!_.isNil(prev)) {
          return { ...prev, reactFlow: flow };
        }
        return null;
      });
    } else {
      if (res?.error.message === ErrorMessages.EMPTY_FLOW_NOT_SAVED || res?.error.name === 'CanceledError') {
        // do nothing
      } else if (String(res?.error.message || '').startsWith(FLOW_REMAP_REFUSED)) {
        // Der Server hat den Ablauf nicht gespeichert, weil laufende Kontakte auf einem
        // entfernten Schritt ohne Nachfolger stünden. Hinweis zeigen und den gespeicherten
        // Ablauf zurückholen — sonst bliebe der Knoten im Editor gelöscht, gespeichert ist er nicht.
        window.alert(res?.error.message);
        await refetchCampaign();
      } else if (res?.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res?.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
        handleOpenCopilotLicenseExpiredDialog?.();
      } else {
        handleOpenErrorDialog(res?.error, '/campaigns/:id', 'patch');
      }
    }

    setUpdateCampaignLoading(false);
  };

  /**
   * Refetch campaign
   */

  const refetchCampaign = async () => {
    if (_.isNil(campaignId)) return;

    const response = await getFunnelById(campaignId);

    if (!response.error) {
      setCampaign(response);

      const { reactFlow } = response;

      if (!_.isNil(reactFlow)) {
        const { viewport } = reactFlow;
        const { x = 0, y = 0, zoom = 1 } = viewport;
        setViewport({ x, y, zoom });

        setNodes(reactFlow?.nodes || []);
        setEdges(reactFlow?.edges || []);
      }
    } else {
      handleOpenErrorDialog(response?.error, '/campaigns/:id', 'get');
    }
  };

  /**
   * Ask for sync dialog handlers
   */

  const handleOpenAskForSyncDialog = (accountId: string, userEmail: string) => {
    setAskForSyncDialog((prev) => ({ ...prev, open: true, accountId, userEmail }));
  };

  const handleCloseAskForSyncDialog = () => {
    setAskForSyncDialog({ open: false, loading: false, accountId: null, userEmail: null });
  };

  const handleSyncLinkedinAccount = async () => {
    const { accountId } = askForSyncDialog;
    if (_.isNil(accountId)) return;

    setAskForSyncDialog((prev) => ({ ...prev, loading: true }));

    await initSyncForLinkedinAccountById(accountId);

    handleCloseAskForSyncDialog();
  };

  return {
    campaign,
    campaignId,
    campaignLoading,
    updateCampaignLoading,
    setCampaign,
    setCampaignWithId,
    setCampaignQueryId,
    handleResetCampaign,
    handleUpdateCampaign,
    handleUpdateCampaignReactFlow,
    fullLinkedinAccount,
    linkedinAccountLoading,
    linkedinAccountUpdating,
    refetchCampaign,
    askForSyncDialog,
    handleOpenAskForSyncDialog,
    handleCloseAskForSyncDialog,
    handleSyncLinkedinAccount,
  };
};

export default useCampaign;
